import React, { useState, useEffect, useContext } from 'react';
import { Popover, Button, Select, message, Spin, InputNumber, Switch, Checkbox } from 'antd';
import { SettingOutlined, UserAddOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { Context } from '../context';
import { getAllLanguages, Flag, getLanguageName } from '../utils';

const TASK_TYPES = {
  'auto_translation': 'Автоперевод',
  'translation_proofreading': 'Проверка перевода',
  'voicing': 'Озвучка',
  'voicing_proofreading': 'Проверка озвучки',
  'animations_video': 'Видео',
  'animations_video_proofreading': 'Проверка видео',
  'full_video': 'Полное видео',
  'full_video_proofreading': 'Проверка полного видео',
  'ts_playlist': 'Плейлисты',
  'loading_in_s3': 'Загрузка в S3'
};

const STATUSES = {
  'done': { title: 'Готово', color: '#52c41a' },
  'in_progress': { title: 'В процессе', color: '#faad14' },
  'pending': { title: 'Не назначен', color: '#8c8c8c' }
};

const BulkActionsPopoverComponent = ({
  projectId,
  partsCount,
  taskTypes = [],
  categoryGroups = null, // [{name: 'Перевод', types: ['auto_translation', 'translation_proofreading']}, ...]
  defaultCategory = null, // which category to pre-check when opening
  columnName,
  onAssigned,
  onUpdated,
  simplified = false // Упрощённый режим: без частей и исполнителя (для полного видео)
}) => {
  const context = useContext(Context);
  const { people, bulkAssignTaskType, bulkUpdateStatus, refreshProject } = context;

  const [isOpen, setIsOpen] = useState(false);

  // Category selection (only used when categoryGroups is provided)
  const [selectedCategories, setSelectedCategories] = useState([]);

  // Compute effective task types based on selected categories or flat taskTypes
  const effectiveTaskTypes = categoryGroups
    ? categoryGroups
        .filter(g => selectedCategories.includes(g.name))
        .flatMap(g => g.types)
    : taskTypes;

  // In combined mode: multi-select array. In classic mode: single value.
  const [selectedTaskTypes, setSelectedTaskTypes] = useState([]);
  const [selectedTaskType, setSelectedTaskType] = useState(taskTypes[0] || null);
  const [selectedLanguage, setSelectedLanguage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [useRange, setUseRange] = useState(false);
  const [partFrom, setPartFrom] = useState(1);
  const [partTo, setPartTo] = useState(partsCount);

  // Assign user state
  const [enableAssign, setEnableAssign] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);

  // Status state
  const [enableStatus, setEnableStatus] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState(null);

  // Pre-check default category when popover opens
  useEffect(() => {
    if (isOpen && categoryGroups && defaultCategory) {
      setSelectedCategories([defaultCategory]);
      // Pre-select all types from the default category
      const defaultTypes = categoryGroups.find(g => g.name === defaultCategory)?.types || [];
      setSelectedTaskTypes(defaultTypes);
    }
  }, [isOpen]);

  // When categories change, update selectedTaskTypes to include all types from selected categories
  useEffect(() => {
    if (categoryGroups) {
      const allTypes = categoryGroups
        .filter(g => selectedCategories.includes(g.name))
        .flatMap(g => g.types);
      setSelectedTaskTypes(allTypes);
    }
  }, [selectedCategories.join(',')]);

  // Helper function to parse groupName (comma-separated string to array)
  const parseGroupName = (groupName) => {
    if (!groupName) return [];
    return groupName.split(',').map(g => g.trim()).filter(Boolean);
  };

  // Filter users by selected language
  const filteredPeople = selectedLanguage
    ? people.filter(p => parseGroupName(p.groupName).includes(getLanguageName(selectedLanguage)))
    : people;

  const handleCategoryChange = (categoryName, checked) => {
    setSelectedCategories(prev => {
      if (checked) {
        return [...prev, categoryName];
      } else {
        return prev.filter(c => c !== categoryName);
      }
    });
  };

  const handleApply = async () => {
    // Validate categories in combined mode
    if (categoryGroups && selectedCategories.length === 0) {
      message.warning('Выберите хотя бы одну категорию');
      return;
    }

    const effectiveEnableStatus = enableStatus || simplified;

    if (!enableAssign && !effectiveEnableStatus) {
      message.warning(simplified ? 'Выберите статус' : 'Выберите хотя бы одно действие: назначить исполнителя или изменить статус');
      return;
    }

    if (categoryGroups && selectedTaskTypes.length === 0) {
      message.warning('Выберите хотя бы один тип задачи');
      return;
    }

    if (!categoryGroups && !selectedTaskType) {
      message.warning('Выберите тип задачи');
      return;
    }

    if (!selectedLanguage) {
      message.warning('Выберите язык');
      return;
    }

    if (enableAssign && !selectedUserId) {
      message.warning('Выберите исполнителя');
      return;
    }

    if (effectiveEnableStatus && !selectedStatus) {
      message.warning('Выберите статус');
      return;
    }

    // Determine which task types to apply to
    const typesToApply = categoryGroups
      ? selectedTaskTypes
      : [selectedTaskType];

    if (typesToApply.length === 0) {
      message.warning('Нет типов задач для применения');
      return;
    }

    setLoading(true);
    try {
      const rangeInfo = useRange ? ` (части ${partFrom}-${partTo})` : '';
      const isMultiType = typesToApply.length > 1;
      let totalAssigned = 0;
      let totalStatusUpdated = 0;

      for (const taskType of typesToApply) {
        // Skip refresh for intermediate calls in multi-type mode
        if (enableAssign) {
          const assignResult = await bulkAssignTaskType(
            projectId,
            taskType,
            selectedUserId,
            selectedLanguage,
            useRange ? partFrom : null,
            useRange ? partTo : null,
            isMultiType // skipRefresh
          );
          totalAssigned += assignResult.updated_count || 0;
          if (!isMultiType && onAssigned) {
            onAssigned(assignResult);
          }
        }

        if (effectiveEnableStatus) {
          const statusResult = await bulkUpdateStatus(
            projectId,
            taskType,
            selectedStatus,
            selectedLanguage,
            useRange ? partFrom : null,
            useRange ? partTo : null,
            isMultiType // skipRefresh
          );
          totalStatusUpdated += statusResult.updated_count || 0;
          if (!isMultiType && onUpdated) {
            onUpdated(statusResult);
          }
        }
      }

      // For multi-type mode, refresh once at the end
      if (isMultiType) {
        await refreshProject(projectId);
        if (enableAssign && onAssigned) onAssigned();
        if (effectiveEnableStatus && onUpdated) onUpdated();
      }

      // Build success message
      const resultParts = [];
      if (enableAssign) resultParts.push(`Назначено на ${totalAssigned} задач`);
      if (effectiveEnableStatus) resultParts.push(`Статус обновлён на ${totalStatusUpdated} задач`);

      const label = categoryGroups
        ? selectedTaskTypes.map(t => TASK_TYPES[t] || t).join(', ')
        : (TASK_TYPES[selectedTaskType] || selectedTaskType);

      message.success(`${label}${rangeInfo}: ${resultParts.join(', ')}`);
      setIsOpen(false);
      resetForm();
    } catch (error) {
      message.error('Ошибка: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setSelectedUserId(null);
    setSelectedStatus(null);
    setEnableAssign(false);
    setEnableStatus(false);
    if (categoryGroups) {
      setSelectedCategories([]);
      setSelectedTaskTypes([]);
    }
  };

  const handleCancel = () => {
    setIsOpen(false);
    if (!categoryGroups) {
      setSelectedTaskType(taskTypes[0] || null);
    }
    setSelectedLanguage(null);
    setUseRange(false);
    setPartFrom(1);
    setPartTo(partsCount);
    resetForm();
  };

  // Calculate parts in range
  const getPartsInRange = () => {
    if (!useRange) return partsCount;
    return Math.max(0, partTo - partFrom + 1);
  };

  // Get user initials
  const getUserInitials = (user) => {
    if (!user) return '?';
    if (user.name) {
      return user.name.charAt(0).toUpperCase();
    }
    if (user.email) {
      return user.email.split('@')[0].charAt(0).toUpperCase();
    }
    return '?';
  };

  // Get user display name
  const getUserDisplayName = (user) => {
    if (!user) return 'Unknown';
    return user.name || user.email?.split('@')[0] || 'Unknown';
  };

  const content = (
    <div style={{ width: '340px' }}>
      <Spin spinning={loading}>
        {!simplified && (
          <div style={{ marginBottom: '8px', fontWeight: 500, fontSize: '13px', color: '#666' }}>
            Применить к {useRange ? `частям ${partFrom}-${partTo}` : `всем ${partsCount} частям`}
          </div>
        )}

        {/* Range selection */}
        {!simplified && (
          <div style={{ marginBottom: '12px' }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <span style={{ fontSize: '12px', color: '#888' }}>Выбрать диапазон:</span>
              <Switch
                size="small"
                checked={useRange}
                onChange={setUseRange}
              />
            </div>
            {useRange && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '6px',
                marginTop: '6px',
                backgroundColor: '#f5f5f5',
                borderRadius: '4px'
              }}>
                <span style={{ fontSize: '11px', color: '#666' }}>с</span>
                <InputNumber
                  size="small"
                  min={1}
                  max={partsCount}
                  value={partFrom}
                  onChange={(val) => setPartFrom(val || 1)}
                  style={{ width: '60px' }}
                />
                <span style={{ fontSize: '11px', color: '#666' }}>по</span>
                <InputNumber
                  size="small"
                  min={1}
                  max={partsCount}
                  value={partTo}
                  onChange={(val) => setPartTo(val || partsCount)}
                  style={{ width: '60px' }}
                />
                <span style={{ fontSize: '10px', color: '#888' }}>
                  ({getPartsInRange()} шт)
                </span>
              </div>
            )}
          </div>
        )}

        {/* Category checkboxes (combined mode) */}
        {categoryGroups && (
          <div style={{ marginBottom: '12px' }}>
            <div style={{ marginBottom: '4px', fontSize: '12px', color: '#888' }}>
              Категории: <span style={{ color: '#ff4d4f' }}>*</span>
            </div>
            <div style={{
              display: 'flex',
              gap: '8px',
              padding: '8px',
              backgroundColor: '#fafafa',
              borderRadius: '6px',
              border: '1px solid #d9d9d9'
            }}>
              {categoryGroups.map(group => (
                <Checkbox
                  key={group.name}
                  checked={selectedCategories.includes(group.name)}
                  onChange={(e) => handleCategoryChange(group.name, e.target.checked)}
                >
                  <div>
                    <div style={{ fontSize: '13px', fontWeight: 500 }}>{group.name}</div>
                    <div style={{ fontSize: '10px', color: '#888', lineHeight: 1.2 }}>
                      {group.types.map(t => TASK_TYPES[t]).join(', ')}
                    </div>
                  </div>
                </Checkbox>
              ))}
            </div>
          </div>
        )}

        {/* Task type selection */}
        {categoryGroups ? (
          // Combined mode: multi-select from available types
          effectiveTaskTypes.length > 0 && (
            <div style={{ marginBottom: '12px' }}>
              <div style={{
                marginBottom: '4px',
                fontSize: '12px',
                color: '#888',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <span>Типы задач: <span style={{ color: '#ff4d4f' }}>*</span></span>
                <a
                  style={{ fontSize: '11px', cursor: 'pointer' }}
                  onClick={() => {
                    if (selectedTaskTypes.length === effectiveTaskTypes.length) {
                      setSelectedTaskTypes([]);
                    } else {
                      setSelectedTaskTypes([...effectiveTaskTypes]);
                    }
                  }}
                >
                  {selectedTaskTypes.length === effectiveTaskTypes.length ? 'Снять все' : 'Выбрать все'}
                </a>
              </div>
              <Select
                mode="multiple"
                value={selectedTaskTypes}
                onChange={setSelectedTaskTypes}
                style={{ width: '100%' }}
                size="small"
                placeholder="Выберите типы задач"
                maxTagCount="responsive"
                options={effectiveTaskTypes.map(type => ({
                  value: type,
                  label: TASK_TYPES[type] || type
                }))}
              />
            </div>
          )
        ) : (
          // Classic mode: show when multiple task types
          taskTypes.length > 1 && (
            <div style={{ marginBottom: '12px' }}>
              <div style={{ marginBottom: '4px', fontSize: '12px', color: '#888' }}>
                Тип задачи:
              </div>
              <Select
                value={selectedTaskType}
                onChange={setSelectedTaskType}
                style={{ width: '100%' }}
                size="small"
                options={taskTypes.map(type => ({
                  value: type,
                  label: TASK_TYPES[type] || type
                }))}
              />
            </div>
          )
        )}

        {/* Language selection */}
        <div style={{ marginBottom: '12px' }}>
          <div style={{ marginBottom: '4px', fontSize: '12px', color: '#888' }}>
            Язык: <span style={{ color: '#ff4d4f' }}>*</span>
          </div>
          <Select
            value={selectedLanguage}
            onChange={(value) => {
              setSelectedLanguage(value);
              setSelectedUserId(null);
            }}
            style={{ width: '100%' }}
            size="small"
            placeholder="Выберите язык"
            options={getAllLanguages().map(langId => ({
              value: langId,
              label: (
                <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Flag langId={langId} height={12} />
                  <span>{getLanguageName(langId)}</span>
                </span>
              )
            }))}
          />
        </div>

        {/* Divider */}
        <div style={{ borderTop: '1px solid #e8e8e8', margin: '12px 0' }} />

        {/* Assign user section */}
        {!simplified && (
          <div style={{ marginBottom: '12px' }}>
            <Checkbox
              checked={enableAssign}
              onChange={(e) => {
                setEnableAssign(e.target.checked);
                if (!e.target.checked) setSelectedUserId(null);
              }}
              style={{ marginBottom: '8px' }}
            >
              <span style={{ fontSize: '13px', fontWeight: 500 }}>
                <UserAddOutlined style={{ marginRight: '4px' }} />
                Назначить исполнителя
              </span>
            </Checkbox>

            {enableAssign && (
              <div style={{
                maxHeight: '150px',
                overflowY: 'auto',
                border: '1px solid #d9d9d9',
                borderRadius: '6px',
                padding: '4px',
                backgroundColor: '#fafafa'
              }}>
                {!selectedLanguage ? (
                  <div style={{ padding: '8px', color: '#999', textAlign: 'center', fontSize: '12px' }}>
                    Сначала выберите язык
                  </div>
                ) : filteredPeople.length === 0 ? (
                  <div style={{ padding: '8px', color: '#999', textAlign: 'center', fontSize: '12px' }}>
                    Нет пользователей в группе {getLanguageName(selectedLanguage)}
                  </div>
                ) : (
                  filteredPeople.map(user => (
                    <div
                      key={user.id}
                      onClick={() => setSelectedUserId(user.id)}
                      style={{
                        padding: '6px 8px',
                        cursor: 'pointer',
                        backgroundColor: selectedUserId === user.id ? '#e6f7ff' : 'transparent',
                        border: selectedUserId === user.id ? '1px solid #1890ff' : '1px solid transparent',
                        borderRadius: '4px',
                        marginBottom: '2px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        if (selectedUserId !== user.id) {
                          e.currentTarget.style.backgroundColor = '#f5f5f5';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (selectedUserId !== user.id) {
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }
                      }}
                    >
                      <div style={{
                        width: '24px',
                        height: '24px',
                        borderRadius: '50%',
                        backgroundColor: selectedUserId === user.id ? '#1890ff' : '#e0e0e0',
                        color: selectedUserId === user.id ? '#fff' : '#666',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '11px',
                        fontWeight: 'bold'
                      }}>
                        {getUserInitials(user)}
                      </div>
                      <span style={{ fontSize: '12px' }}>
                        {getUserDisplayName(user)}
                      </span>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        )}

        {/* Status section */}
        <div style={{ marginBottom: '12px' }}>
          {!simplified && (
            <Checkbox
              checked={enableStatus}
              onChange={(e) => {
                setEnableStatus(e.target.checked);
                if (!e.target.checked) setSelectedStatus(null);
              }}
              style={{ marginBottom: '8px' }}
            >
              <span style={{ fontSize: '13px', fontWeight: 500 }}>
                <CheckCircleOutlined style={{ marginRight: '4px' }} />
                Изменить статус
              </span>
            </Checkbox>
          )}
          {simplified && (
            <div style={{ marginBottom: '8px', fontSize: '12px', color: '#888' }}>
              Статус: <span style={{ color: '#ff4d4f' }}>*</span>
            </div>
          )}

          {(enableStatus || simplified) && (
            <div style={{ display: 'flex', gap: '6px' }}>
              {Object.entries(STATUSES).map(([status, config]) => (
                <div
                  key={status}
                  onClick={() => setSelectedStatus(status)}
                  style={{
                    flex: 1,
                    padding: '6px 8px',
                    cursor: 'pointer',
                    backgroundColor: selectedStatus === status ? '#e6f7ff' : '#fafafa',
                    border: selectedStatus === status ? '1px solid #1890ff' : '1px solid #d9d9d9',
                    borderRadius: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '4px',
                    transition: 'all 0.2s'
                  }}
                >
                  <div style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    backgroundColor: config.color
                  }} />
                  <span style={{ fontSize: '11px' }}>{config.title}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Buttons */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '16px' }}>
          <Button size="small" onClick={handleCancel}>
            Отмена
          </Button>
          <Button
            size="small"
            type="primary"
            onClick={handleApply}
            disabled={
              (!enableAssign && !enableStatus && !simplified) ||
              !selectedLanguage ||
              (categoryGroups && selectedCategories.length === 0) ||
              (categoryGroups && selectedTaskTypes.length === 0) ||
              (enableAssign && !selectedUserId) ||
              ((enableStatus || simplified) && !selectedStatus) ||
              (!simplified && useRange && getPartsInRange() <= 0)
            }
            icon={<SettingOutlined />}
          >
            {simplified ? 'Применить' : `Применить на ${getPartsInRange()} ${getPartsInRange() === 1 ? 'часть' : 'частей'}`}
          </Button>
        </div>
      </Spin>
    </div>
  );

  return (
    <Popover
      content={content}
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <SettingOutlined />
          <span>{columnName}</span>
        </div>
      }
      trigger="click"
      open={isOpen}
      onOpenChange={setIsOpen}
      placement="bottomLeft"
      destroyTooltipOnHide
    >
      <div
        className="t_th_step_interactive"
        style={{ cursor: 'pointer' }}
        onClick={(e) => e.stopPropagation()}
      >
        {columnName}
      </div>
    </Popover>
  );
};

export const BulkActionsPopover = React.memo(BulkActionsPopoverComponent);
